// src/components/Navbar.jsx
import React, {
  useState, useEffect, useRef, useCallback, useMemo, memo,
} from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useChat } from '../context/ChatContext';
import { auth, db } from '../firebase';
import { signOut } from 'firebase/auth';
import {
  collection, query, orderBy, limit, onSnapshot, doc,
  updateDoc, writeBatch, arrayUnion,
} from 'firebase/firestore';

/* ================================================================== */
/* Constants                                                           */
/* ================================================================== */

const RECENT_SEARCHES_KEY = 'recentSearches';
const MAX_RECENT_SEARCHES = 6;

/**
 * Canonical event name for opening the post composer.
 * Kept exported for backwards compatibility with other components,
 * even though the navbar no longer triggers it.
 */
const POST_COMPOSER_EVENT = 'open-post-composer';

const SEARCH_RESULT_ITEM_SELECTOR = '[data-search-item="true"]';

const isMac =
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent);

/* ================================================================== */
/* Hooks                                                               */
/* ================================================================== */

/** Reference-counted body scroll lock. */
let scrollLockCount = 0;
const useBodyScrollLock = (active) => {
  useEffect(() => {
    if (!active) return undefined;
    if (scrollLockCount === 0) document.body.style.overflow = 'hidden';
    scrollLockCount += 1;
    return () => {
      scrollLockCount = Math.max(0, scrollLockCount - 1);
      if (scrollLockCount === 0) document.body.style.overflow = '';
    };
  }, [active]);
};

/** Debounce a value. */
const useDebounced = (value, delay = 200) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
};

/** Close on outside click. */
const useClickOutside = (ref, onOutside, enabled = true) => {
  useEffect(() => {
    if (!enabled) return undefined;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onOutside?.();
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [ref, onOutside, enabled]);
};

/** Recent searches with cross-tab sync. */
const useRecentSearches = () => {
  const [items, setItems] = useState(() => loadRecentSearches());

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== RECENT_SEARCHES_KEY) return;
      setItems(loadRecentSearches());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const add = useCallback((term) => {
    const next = saveRecentSearch(term);
    setItems(next);
    return next;
  }, []);

  const remove = useCallback((term) => {
    const next = removeRecentSearch(term);
    setItems(next);
    return next;
  }, []);

  const clear = useCallback(() => {
    const next = clearRecentSearches();
    setItems(next);
    return next;
  }, []);

  return { items, add, remove, clear };
};

/* ================================================================== */
/* Highlight helper                                                    */
/* ================================================================== */

const highlightMatch = (text, term) => {
  if (!term || !text) return text;
  const q = term.trim();
  if (!q) return text;

  const regex = new RegExp(
    `(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
    'gi',
  );
  const parts = String(text).split(regex);

  return parts.map((part, i) =>
    part.toLowerCase() === q.toLowerCase() ? (
      <mark
        key={i}
        className="bg-orange/25 text-orange font-semibold rounded px-0.5"
      >
        {part}
      </mark>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    ),
  );
};

const truncate = (str, n = 80) => {
  if (!str) return '';
  return str.length > n ? `${str.slice(0, n)}…` : str;
};

/* ================================================================== */
/* Recent searches storage                                             */
/* ================================================================== */

const loadRecentSearches = () => {
  try {
    const raw = localStorage.getItem(RECENT_SEARCHES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const saveRecentSearch = (term) => {
  const q = term.trim();
  if (!q || q.length < 2) return loadRecentSearches();
  try {
    const existing = loadRecentSearches();
    const filtered = existing.filter(
      (t) => t.toLowerCase() !== q.toLowerCase(),
    );
    const next = [q, ...filtered].slice(0, MAX_RECENT_SEARCHES);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(next));
    return next;
  } catch {
    return loadRecentSearches();
  }
};

const removeRecentSearch = (term) => {
  try {
    const existing = loadRecentSearches();
    const next = existing.filter((t) => t !== term);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(next));
    return next;
  } catch {
    return loadRecentSearches();
  }
};

const clearRecentSearches = () => {
  try {
    localStorage.removeItem(RECENT_SEARCHES_KEY);
  } catch {
    /* noop */
  }
  return [];
};

/* ================================================================== */
/* Role normalization                                                  */
/* ================================================================== */

const normalizeRole = (r) => String(r || '').trim().toLowerCase();

/* ================================================================== */
/* Small helpers                                                       */
/* ================================================================== */

const timeGroupLabel = (date) => {
  if (!date) return 'Earlier';
  const now = new Date();
  const d = new Date(date);
  const sameDay = now.toDateString() === d.toDateString();
  if (sameDay) return 'Today';
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (yesterday.toDateString() === d.toDateString()) return 'Yesterday';
  if (now - d < 7 * 24 * 60 * 60 * 1000) return 'This week';
  return 'Earlier';
};

/**
 * Dispatch the "open post composer" event.
 * Kept exported for backwards compatibility — the navbar itself
 * no longer triggers this, since the floating composer is removed.
 */
export const openPostComposer = (source = 'navbar') => {
  try {
    window.dispatchEvent(
      new CustomEvent(POST_COMPOSER_EVENT, {
        detail: { source, ts: Date.now() },
      }),
    );
  } catch (err) {
    console.warn('openPostComposer failed:', err);
  }
};

/* ================================================================== */
/* Navbar                                                              */
/* ================================================================== */

const Navbar = () => {
  const {
    user,
    isAdmin,
    isBoard,
    isMember,
    isApproved,
    userData,
    loading,
    isPendingApproval,
  } = useAuth();
  const { totalUnreadCount, getUserOnlineStatus } = useChat();
  const location = useLocation();
  const navigate = useNavigate();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [showNavbar, setShowNavbar] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifDropdownOpen, setNotifDropdownOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [userPhoto, setUserPhoto] = useState(null);

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchTab, setSearchTab] = useState('all');
  const [activeResultIndex, setActiveResultIndex] = useState(-1);

  const [members, setMembers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [stories, setStories] = useState([]);

  const {
    items: recentSearches,
    add: addRecent,
    remove: removeRecent,
    clear: clearRecent,
  } = useRecentSearches();
  const debouncedSearch = useDebounced(searchTerm, 200);

  const notifDropdownRef = useRef(null);
  const profileDropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchContainerRef = useRef(null);
  const searchResultsRef = useRef(null);
  const lastScrollYRef = useRef(0);
  const prevUnreadCountRef = useRef(null);
  const audioContextRef = useRef(null);
  const mountedRef = useRef(true);

  const isChatPage = location.pathname === '/chat';
  const isHomePage = location.pathname === '/';

  const isUserApproved =
    isApproved === true ||
    userData?.approved === true ||
    userData?.isApproved === true ||
    userData?.registrationStatus === 'approved' ||
    isAdmin ||
    isBoard;

  const isUserPending =
    !isUserApproved &&
    (isPendingApproval?.() ||
      userData?.registrationStatus === 'pending_approval');

  const effectiveRole = useMemo(() => {
    if (isAdmin) return 'admin';
    if (isBoard) return 'board';
    if (isMember) return 'member';
    return normalizeRole(userData?.role) || 'member';
  }, [isAdmin, isBoard, isMember, userData?.role]);

  const searchResults = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q || q.length < 2) {
      return { members: [], posts: [], stories: [], total: 0 };
    }

    const memberResults = members
      .filter((m) => m.id !== user?.uid)
      .filter((m) => {
        const hay = [m.displayName, m.name, m.email, m.company, m.position]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 6);

    const postResults = posts
      .filter((p) => {
        const hay = [p.content, p.authorName]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 6);

    const storyResults = stories
      .filter((s) => {
        const hay = [s.caption, s.authorName]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 6);

    return {
      members: memberResults,
      posts: postResults,
      stories: storyResults,
      total: memberResults.length + postResults.length + storyResults.length,
    };
  }, [debouncedSearch, members, posts, stories, user?.uid]);

  const flatResults = useMemo(() => {
    const out = [];
    if (searchTab === 'all' || searchTab === 'members') {
      searchResults.members.forEach((m) =>
        out.push({ type: 'member', payload: m }),
      );
    }
    if (searchTab === 'all' || searchTab === 'posts') {
      searchResults.posts.forEach((p) =>
        out.push({ type: 'post', payload: p }),
      );
    }
    if (searchTab === 'all' || searchTab === 'stories') {
      searchResults.stories.forEach((s) =>
        out.push({ type: 'story', payload: s }),
      );
    }
    return out;
  }, [searchResults, searchTab]);

  const tabs = useMemo(
    () => [
      { id: 'all', label: 'All', count: searchResults.total },
      { id: 'members', label: 'Members', count: searchResults.members.length },
      { id: 'posts', label: 'Posts', count: searchResults.posts.length },
      { id: 'stories', label: 'Stories', count: searchResults.stories.length },
    ],
    [searchResults],
  );

  const groupedNotifications = useMemo(() => {
    const groups = {};
    const order = ['Today', 'Yesterday', 'This week', 'Earlier'];
    notifications.slice(0, 20).forEach((n) => {
      const label = timeGroupLabel(n.createdAt?.toDate?.());
      if (!groups[label]) groups[label] = [];
      groups[label].push(n);
    });
    return order
      .filter((k) => groups[k]?.length)
      .map((k) => ({ label: k, items: groups[k] }));
  }, [notifications]);

  const playNotificationSound = useCallback(() => {
    if (!isUserApproved) return;
    try {
      if (!audioContextRef.current) {
        const Ctor = window.AudioContext || window.webkitAudioContext;
        if (!Ctor) return;
        audioContextRef.current = new Ctor();
      }
      const ctx = audioContextRef.current;
      if (!ctx || ctx.state === 'closed') return;

      const beep = () => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = 800;
        gain.gain.value = 0.1;
        osc.start();
        gain.gain.exponentialRampToValueAtTime(
          0.00001,
          ctx.currentTime + 0.5,
        );
        osc.stop(ctx.currentTime + 0.5);
      };
      if (ctx.state === 'suspended') ctx.resume().then(beep).catch(() => {});
      else beep();
    } catch (err) {
      console.warn('Could not play notification sound', err);
    }
  }, [isUserApproved]);

  const toggleDark = useCallback(() => {
    setDarkMode((prev) => {
      const next = !prev;
      const root = document.documentElement;
      if (next) {
        root.classList.add('dark');
        localStorage.setItem('theme', 'dark');
      } else {
        root.classList.remove('dark');
        localStorage.setItem('theme', 'light');
      }
      return next;
    });
  }, []);

  const handleNotificationClick = useCallback(
    async (notif) => {
      if (!user || !notif) return;
      const alreadyRead = notif.readBy?.includes(user.uid);

      if (!alreadyRead) {
        try {
          await updateDoc(doc(db, 'notifications', notif.id), {
            readBy: arrayUnion(user.uid),
          });
          if (mountedRef.current) {
            setNotifications((prev) =>
              prev.map((n) =>
                n.id === notif.id
                  ? { ...n, readBy: [...(n.readBy || []), user.uid] }
                  : n,
              ),
            );
            setUnreadCount((c) => Math.max(0, c - 1));
          }
        } catch (err) {
          console.error('markAsRead failed:', err);
        }
      }
      if (notif.link) {
        setNotifDropdownOpen(false);
        if (/^https?:\/\//i.test(notif.link)) {
          window.open(notif.link, '_blank', 'noopener,noreferrer');
        } else {
          navigate(notif.link);
        }
      }
    },
    [user, navigate],
  );

  const markAllAsRead = useCallback(async () => {
    if (!user) return;
    const unread = notifications.filter((n) => !n.readBy?.includes(user.uid));
    if (unread.length === 0) return;

    try {
      for (let i = 0; i < unread.length; i += 450) {
        const batch = writeBatch(db);
        unread.slice(i, i + 450).forEach((n) =>
          batch.update(doc(db, 'notifications', n.id), {
            readBy: arrayUnion(user.uid),
          }),
        );
        await batch.commit();
      }
      if (!mountedRef.current) return;
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, readBy: [...(n.readBy || []), user.uid] })),
      );
      setUnreadCount(0);
    } catch (err) {
      console.error('markAllAsRead failed:', err);
    }
  }, [user, notifications]);

  const handleLogout = useCallback(async () => {
    try {
      await signOut(auth);
    } catch (err) {
      console.error('logout failed:', err);
    } finally {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
    setShowSearchResults(false);
    setActiveResultIndex(-1);
  }, []);

  const removeRecentItem = useCallback(
    (term, e) => {
      e?.stopPropagation?.();
      removeRecent(term);
    },
    [removeRecent],
  );

  const clearAllRecent = useCallback(
    (e) => {
      e?.stopPropagation?.();
      clearRecent();
    },
    [clearRecent],
  );

  const applyRecentSearch = useCallback((term) => {
    setSearchTerm(term);
    setShowSearchResults(true);
    setTimeout(() => searchInputRef.current?.focus(), 50);
  }, []);

  const goToResult = useCallback(
    (item) => {
      const q = searchTerm.trim();
      if (q) addRecent(q);
      setShowSearchResults(false);
      setSearchTerm('');
      setSearchOpen(false);
      setActiveResultIndex(-1);

      switch (item.type) {
        case 'member':
          navigate(`/profile/${item.payload.id}`);
          break;
        case 'post':
          navigate('/', { state: { highlightPostId: item.payload.id } });
          break;
        case 'story':
          navigate(`/profile/${item.payload.authorId}`);
          break;
        default:
          break;
      }
    },
    [searchTerm, addRecent, navigate],
  );

  const handleSearchKeyDown = useCallback(
    (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveResultIndex((i) =>
          Math.min(flatResults.length - 1, i + 1),
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveResultIndex((i) => Math.max(-1, i - 1));
      } else if (e.key === 'Enter') {
        if (activeResultIndex >= 0 && flatResults[activeResultIndex]) {
          e.preventDefault();
          goToResult(flatResults[activeResultIndex]);
        }
      } else if (e.key === 'Escape') {
        clearSearch();
        searchInputRef.current?.blur();
      }
    },
    [flatResults, activeResultIndex, goToResult, clearSearch],
  );

  const getUserDisplayName = useCallback(
    () =>
      user?.displayName ||
      (user?.email ? user.email.split('@')[0] : null) ||
      'User',
    [user],
  );

  const getUserInitial = useCallback(() => {
    const n = user?.displayName || user?.email || 'U';
    return n.charAt(0).toUpperCase();
  }, [user]);

  const getUserRole = useCallback(() => {
    if (isAdmin) return 'Admin';
    if (isBoard) return 'Board';
    if (isMember) return 'Member';
    return '';
  }, [isAdmin, isBoard, isMember]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useBodyScrollLock(mobileOpen || searchOpen);

  useEffect(() => {
    setMobileOpen(false);
    setProfileDropdownOpen(false);
    setNotifDropdownOpen(false);
    setSearchOpen(false);
    setShowSearchResults(false);
    setSearchTerm('');
    setActiveResultIndex(-1);
  }, [location.pathname]);

  useEffect(() => {
    return () => {
      const ctx = audioContextRef.current;
      if (ctx) {
        try {
          if (ctx.state !== 'closed') ctx.close?.();
        } catch {
          /* noop */
        }
        audioContextRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!user || !isUserApproved) return;
    if (prevUnreadCountRef.current === null) {
      prevUnreadCountRef.current = totalUnreadCount;
      return;
    }
    if (totalUnreadCount > prevUnreadCountRef.current && !isChatPage) {
      playNotificationSound();
    }
    prevUnreadCountRef.current = totalUnreadCount;
  }, [
    totalUnreadCount,
    isChatPage,
    user,
    isUserApproved,
    playNotificationSound,
  ]);

  useEffect(() => {
    const root = document.documentElement;
    const stored = localStorage.getItem('theme');
    if (stored === 'dark') {
      setDarkMode(true);
      root.classList.add('dark');
    } else if (stored === 'light') {
      setDarkMode(false);
      root.classList.remove('dark');
    } else if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      setDarkMode(true);
      root.classList.add('dark');
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setUserPhoto(null);
      return undefined;
    }
    const refDoc = doc(db, 'users', user.uid);
    const unsub = onSnapshot(
      refDoc,
      (snap) => {
        if (!mountedRef.current) return;
        setUserPhoto(snap.exists() ? snap.data().photoUrl || null : null);
      },
      (err) => console.warn('user photo listener:', err),
    );
    return () => unsub();
  }, [user]);

  useEffect(() => {
    if (!user || !isUserApproved) {
      setNotifications([]);
      setUnreadCount(0);
      return undefined;
    }

    const q = query(
      collection(db, 'notifications'),
      orderBy('createdAt', 'desc'),
      limit(50),
    );

    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const all = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        const filtered = all.filter((n) => {
          if (n.recipientType === 'all') return true;
          if (n.recipientType === 'roles') {
            const roles = Array.isArray(n.recipients) ? n.recipients : [];
            return roles.some((r) => normalizeRole(r) === effectiveRole);
          }
          if (n.recipientType === 'users') {
            return (
              Array.isArray(n.recipients) && n.recipients.includes(user.uid)
            );
          }
          return false;
        });
        if (!mountedRef.current) return;
        setNotifications(filtered);
        setUnreadCount(
          filtered.filter((n) => !n.readBy?.includes(user.uid)).length,
        );
      },
      (err) => console.warn('notifications listener:', err),
    );

    return () => unsub();
  }, [user, effectiveRole, isUserApproved]);

  useEffect(() => {
    if (!user) return undefined;
    const unsub = onSnapshot(
      collection(db, 'users'),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (!mountedRef.current) return;
        setMembers(
          list.filter(
            (m) => m.isBlocked !== true && m.registrationStatus !== 'rejected',
          ),
        );
      },
      (err) => console.warn('members listener:', err),
    );
    return () => unsub();
  }, [user]);

  useEffect(() => {
    if (!user) return undefined;
    const q = query(
      collection(db, 'posts'),
      orderBy('createdAt', 'desc'),
      limit(60),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (!mountedRef.current) return;
        setPosts(
          list.filter(
            (p) =>
              p.approved === true &&
              (p.visibility === 'public' || p.authorId === user.uid),
          ),
        );
      },
      (err) => console.warn('posts listener:', err),
    );
    return () => unsub();
  }, [user]);

  useEffect(() => {
    if (!user) return undefined;
    const q = query(
      collection(db, 'stories'),
      orderBy('createdAt', 'desc'),
      limit(40),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const now = Date.now();
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (!mountedRef.current) return;
        setStories(
          list.filter((s) => {
            const expires = s.expiresAt?.toDate?.()?.getTime?.() || Infinity;
            const isMine = s.authorId === user.uid;
            return expires > now && (s.approved === true || isMine);
          }),
        );
      },
      (err) => console.warn('stories listener:', err),
    );
    return () => unsub();
  }, [user]);

  useEffect(() => {
    setActiveResultIndex(-1);
  }, [debouncedSearch, searchTab]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchTab('all');
        setSearchOpen(true);
        setTimeout(() => searchInputRef.current?.focus(), 50);
      }
      if (e.key === 'Escape') {
        setSearchOpen(false);
        setShowSearchResults(false);
        setNotifDropdownOpen(false);
        setProfileDropdownOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useClickOutside(
    notifDropdownRef,
    () => setNotifDropdownOpen(false),
    notifDropdownOpen,
  );
  useClickOutside(
    profileDropdownRef,
    () => setProfileDropdownOpen(false),
    profileDropdownOpen,
  );

  useEffect(() => {
    const onPointerDown = (event) => {
      if (searchOpen && window.innerWidth < 768) return;
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target)
      ) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [searchOpen]);

  useEffect(() => {
    const SCROLL_THRESHOLD = 10;

    const handleScroll = () => {
      const y = window.scrollY;
      const prevY = lastScrollYRef.current;

      if (y < 50) {
        setShowNavbar(true);
        lastScrollYRef.current = y;
        return;
      }

      if (isHomePage) {
        setShowNavbar(true);
        lastScrollYRef.current = y;
        return;
      }

      if (Math.abs(y - prevY) < SCROLL_THRESHOLD) return;

      if (y > prevY && y > 80) setShowNavbar(false);
      else if (y < prevY) setShowNavbar(true);

      lastScrollYRef.current = y;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHomePage]);

  useEffect(() => {
    if (activeResultIndex < 0) return;
    const el = searchResultsRef.current?.querySelector(
      `${SEARCH_RESULT_ITEM_SELECTOR}[data-idx="${activeResultIndex}"]`,
    );
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeResultIndex]);

  const navbarBg = darkMode
    ? 'bg-gray-900/95 backdrop-blur-lg border-b border-white/10'
    : 'bg-white/95 backdrop-blur-lg border-b border-gray-200';

  const iconBtnCls = `relative w-10 h-10 rounded-full flex items-center justify-center transition-colors outline-none focus-visible:ring-2 focus-visible:ring-orange ${
    darkMode
      ? 'hover:bg-gray-700 text-gray-200'
      : 'hover:bg-gray-100 text-gray-600'
  }`;

  const isSelfOnline = getUserOnlineStatus(user?.uid);

  const showMembersTab = searchTab === 'all' || searchTab === 'members';
  const showPostsTab = searchTab === 'all' || searchTab === 'posts';
  const showStoriesTab = searchTab === 'all' || searchTab === 'stories';

  if (loading) {
    return (
      <div className="sticky top-0 z-50 bg-white/95 dark:bg-gray-900/95 backdrop-blur-lg border-b border-gray-200 dark:border-white/10 animate-pulse">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
          <div className="h-10 w-32 bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="h-10 flex-1 max-w-md mx-4 bg-gray-200 dark:bg-gray-700 rounded-full hidden md:block" />
          <div className="flex gap-2">
            <div className="h-10 w-10 bg-gray-200 dark:bg-gray-700 rounded-full" />
            <div className="h-10 w-10 bg-gray-200 dark:bg-gray-700 rounded-full" />
          </div>
        </div>
      </div>
    );
  }

  const renderSearchDropdown = () => {
    const q = searchTerm.trim();

    if (q.length < 2) {
      return (
        <div className={darkMode ? 'bg-gray-800' : 'bg-white'}>
          {recentSearches.length > 0 ? (
            <>
              <div className="px-3 py-2 border-b dark:border-gray-700 flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-500">
                  Recent
                </p>
                <button
                  onClick={clearAllRecent}
                  className="text-[10px] text-orange hover:underline"
                >
                  Clear all
                </button>
              </div>
              <div className="p-2 flex flex-wrap gap-1.5">
                {recentSearches.map((term) => (
                  <button
                    key={term}
                    onClick={() => applyRecentSearch(term)}
                    className={`group flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs transition-colors ${
                      darkMode
                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                    }`}
                  >
                    <i
                      className="fas fa-history text-[10px] text-gray-400"
                      aria-hidden="true"
                    />
                    <span className="truncate max-w-[140px]">{term}</span>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => removeRecentItem(term, e)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          removeRecentItem(term, e);
                        }
                      }}
                      className="ml-0.5 opacity-50 hover:opacity-100 cursor-pointer"
                      aria-label={`Remove ${term} from recent`}
                    >
                      <i
                        className="fas fa-times text-[9px]"
                        aria-hidden="true"
                      />
                    </span>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="p-6 text-center text-sm text-gray-500">
              <i
                className="fas fa-search text-2xl mb-2 block opacity-30"
                aria-hidden="true"
              />
              Search for members, posts, and stories
              <p className="text-[10px] text-gray-400 mt-1">
                Tip: press{' '}
                <kbd className="px-1 border rounded">
                  {isMac ? '⌘K' : 'Ctrl K'}
                </kbd>
              </p>
            </div>
          )}
        </div>
      );
    }

    let runningIdx = -1;

    return (
      <div className={darkMode ? 'bg-gray-800' : 'bg-white'}>
        <div
          className={`flex items-center gap-1 px-2 py-1.5 border-b dark:border-gray-700 overflow-x-auto ${
            darkMode ? 'bg-gray-800/80' : 'bg-gray-50'
          }`}
        >
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setSearchTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                searchTab === t.id
                  ? 'bg-orange text-white'
                  : darkMode
                  ? 'text-gray-300 hover:bg-gray-700'
                  : 'text-gray-600 hover:bg-gray-200'
              }`}
            >
              {t.label}
              {t.count > 0 && (
                <span
                  className={`text-[10px] px-1.5 rounded-full ${
                    searchTab === t.id
                      ? 'bg-white/25'
                      : darkMode
                      ? 'bg-gray-700'
                      : 'bg-gray-200'
                  }`}
                >
                  {t.count}
                </span>
              )}
            </button>
          ))}
        </div>

        <div
          ref={searchResultsRef}
          className="max-h-[420px] overflow-y-auto"
          role="listbox"
          aria-label="Search results"
        >
          {searchResults.total === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500">
              <i
                className="fas fa-search-minus text-2xl mb-2 block opacity-40"
                aria-hidden="true"
              />
              No results for "{q}"
            </div>
          ) : (
            <>
              {/* Members */}
              {showMembersTab && searchResults.members.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 border-b dark:border-gray-700">
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-500">
                      Members
                    </p>
                  </div>
                  {searchResults.members.map((m) => {
                    const idx = ++runningIdx;
                    const isActive = activeResultIndex === idx;
                    const displayName =
                      m.displayName || m.name || m.email?.split('@')[0];
                    const initial = displayName?.[0]?.toUpperCase() || 'U';
                    const online = getUserOnlineStatus?.(m.id);
                    return (
                      <button
                        key={m.id}
                        data-search-item="true"
                        data-idx={idx}
                        role="option"
                        aria-selected={isActive}
                        onMouseEnter={() => setActiveResultIndex(idx)}
                        onClick={() =>
                          goToResult({ type: 'member', payload: m })
                        }
                        className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
                          isActive
                            ? darkMode
                              ? 'bg-gray-700'
                              : 'bg-gray-100'
                            : darkMode
                            ? 'hover:bg-gray-700'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="relative flex-shrink-0">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange to-orange-dark text-white flex items-center justify-center font-semibold text-sm overflow-hidden">
                            {m.photoUrl ? (
                              <img
                                src={m.photoUrl}
                                alt=""
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            ) : (
                              initial
                            )}
                          </div>
                          {online && (
                            <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white dark:border-gray-800 rounded-full" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p
                            className={`text-sm font-medium truncate ${
                              darkMode ? 'text-white' : 'text-gray-900'
                            }`}
                          >
                            {highlightMatch(displayName, q)}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {highlightMatch(
                              m.position || m.company || m.email || '',
                              q,
                            )}
                          </p>
                        </div>
                        <i
                          className="fas fa-user text-xs text-gray-400"
                          aria-hidden="true"
                        />
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Posts */}
              {showPostsTab && searchResults.posts.length > 0 && (
                <div className="border-t dark:border-gray-700">
                  <div className="px-3 py-1.5 border-b dark:border-gray-700">
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-500">
                      Posts
                    </p>
                  </div>
                  {searchResults.posts.map((p) => {
                    const idx = ++runningIdx;
                    const isActive = activeResultIndex === idx;
                    const preview = p.content
                      ? truncate(p.content, 100)
                      : p.media?.length
                      ? '📷 Photo/Video post'
                      : '—';
                    const thumb =
                      p.media?.[0]?.url ||
                      (Array.isArray(p.photos) && p.photos[0]) ||
                      null;
                    const thumbType = p.media?.[0]?.type || 'image';
                    return (
                      <button
                        key={p.id}
                        data-search-item="true"
                        data-idx={idx}
                        role="option"
                        aria-selected={isActive}
                        onMouseEnter={() => setActiveResultIndex(idx)}
                        onClick={() =>
                          goToResult({ type: 'post', payload: p })
                        }
                        className={`w-full flex items-start gap-3 px-3 py-2 text-left transition-colors ${
                          isActive
                            ? darkMode
                              ? 'bg-gray-700'
                              : 'bg-gray-100'
                            : darkMode
                            ? 'hover:bg-gray-700'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        {thumb ? (
                          thumbType === 'video' ? (
                            <div className="w-12 h-12 rounded-lg bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0 overflow-hidden">
                              <i
                                className="fas fa-play text-gray-500 text-sm"
                                aria-hidden="true"
                              />
                            </div>
                          ) : (
                            <img
                              src={thumb}
                              alt=""
                              loading="lazy"
                              className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                            />
                          )
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-orange/10 flex items-center justify-center flex-shrink-0">
                            <i
                              className="fas fa-newspaper text-orange text-sm"
                              aria-hidden="true"
                            />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p
                            className={`text-xs ${
                              darkMode ? 'text-gray-400' : 'text-gray-500'
                            }`}
                          >
                            {p.authorName}
                          </p>
                          <p
                            className={`text-sm truncate ${
                              darkMode ? 'text-white' : 'text-gray-900'
                            }`}
                          >
                            {highlightMatch(preview, q)}
                          </p>
                        </div>
                        <i
                          className="fas fa-arrow-right text-xs text-gray-400 mt-1"
                          aria-hidden="true"
                        />
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Stories */}
              {showStoriesTab && searchResults.stories.length > 0 && (
                <div className="border-t dark:border-gray-700">
                  <div className="px-3 py-1.5 border-b dark:border-gray-700">
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-500">
                      Stories
                    </p>
                  </div>
                  {searchResults.stories.map((s) => {
                    const idx = ++runningIdx;
                    const isActive = activeResultIndex === idx;
                    const thumb =
                      s.items?.[0]?.url ||
                      (Array.isArray(s.photos) && s.photos[0]) ||
                      null;
                    const caption = s.caption || '📷 Story';
                    return (
                      <button
                        key={s.id}
                        data-search-item="true"
                        data-idx={idx}
                        role="option"
                        aria-selected={isActive}
                        onMouseEnter={() => setActiveResultIndex(idx)}
                        onClick={() =>
                          goToResult({ type: 'story', payload: s })
                        }
                        className={`w-full flex items-start gap-3 px-3 py-2 text-left transition-colors ${
                          isActive
                            ? darkMode
                              ? 'bg-gray-700'
                              : 'bg-gray-100'
                            : darkMode
                            ? 'hover:bg-gray-700'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        {thumb ? (
                          <img
                            src={thumb}
                            alt=""
                            loading="lazy"
                            className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center flex-shrink-0">
                            <i
                              className="fas fa-images text-pink-500 text-sm"
                              aria-hidden="true"
                            />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p
                            className={`text-xs ${
                              darkMode ? 'text-gray-400' : 'text-gray-500'
                            }`}
                          >
                            {s.authorName}
                          </p>
                          <p
                            className={`text-sm truncate ${
                              darkMode ? 'text-white' : 'text-gray-900'
                            }`}
                          >
                            {highlightMatch(caption, q)}
                          </p>
                        </div>
                        <i
                          className="fas fa-user-circle text-xs text-gray-400 mt-1"
                          aria-hidden="true"
                        />
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <nav
        className={`sticky top-0 z-50 w-full transition-transform duration-300 ease-in-out ${navbarBg} ${
          !showNavbar ? '-translate-y-full' : 'translate-y-0'
        }`}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2 flex items-center gap-2 sm:gap-3">
          {/* LEFT: Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 flex-shrink-0 group"
            aria-label="IIEE-ERCSA Home"
          >
            <img
              src="/images/iiee-logo.png"
              alt="IIEE-ERCSA"
              className="h-9 w-9 object-contain transition-transform duration-200 group-hover:scale-105"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
            <div className="hidden sm:block leading-tight">
              <h1
                className={`text-sm font-bold tracking-tight ${
                  darkMode ? 'text-white' : 'text-gray-900'
                }`}
              >
                IIEE-ERCSA
              </h1>
              <p
                className={`text-[10px] tracking-wide ${
                  darkMode ? 'text-gray-400' : 'text-gray-500'
                }`}
              >
                Eastern Region Chapter
              </p>
            </div>
          </Link>

          {/* CENTER: Search (desktop) */}
          <div
            ref={searchContainerRef}
            className="flex-1 max-w-md mx-auto relative hidden md:block"
          >
            <div className="relative">
              <i
                className={`fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-sm ${
                  darkMode ? 'text-gray-400' : 'text-gray-500'
                }`}
                aria-hidden="true"
              />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setShowSearchResults(true);
                }}
                onFocus={() => setShowSearchResults(true)}
                onKeyDown={handleSearchKeyDown}
                placeholder="Search members, posts, stories..."
                aria-label="Search"
                aria-haspopup="listbox"
                aria-expanded={showSearchResults}
                aria-controls="navbar-search-results"
                autoComplete="off"
                className={`w-full pl-10 pr-16 py-2 rounded-full text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-orange/50 ${
                  darkMode
                    ? 'bg-gray-800 text-white placeholder-gray-500 border border-gray-700'
                    : 'bg-gray-100 text-gray-900 placeholder-gray-500 border border-transparent hover:bg-gray-200'
                }`}
              />
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  aria-label="Clear search"
                  className={`absolute right-10 top-1/2 transform -translate-y-1/2 ${
                    darkMode ? 'text-gray-400' : 'text-gray-500'
                  } hover:text-orange`}
                >
                  <i
                    className="fas fa-times-circle text-sm"
                    aria-hidden="true"
                  />
                </button>
              )}
              <kbd
                className={`absolute right-3 top-1/2 transform -translate-y-1/2 hidden lg:block text-[10px] px-1.5 py-0.5 rounded border ${
                  darkMode
                    ? 'text-gray-500 border-gray-700'
                    : 'text-gray-400 border-gray-300'
                }`}
              >
                {isMac ? '⌘K' : 'Ctrl K'}
              </kbd>
            </div>

            <AnimatePresence>
              {showSearchResults && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.15 }}
                  id="navbar-search-results"
                  className="absolute left-0 right-0 mt-2 rounded-xl shadow-2xl ring-1 ring-black/5 dark:ring-white/10 z-50 overflow-hidden"
                >
                  {renderSearchDropdown()}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* RIGHT: Icons + Avatar */}
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <button
              onClick={() => {
                setSearchTab('all');
                setSearchOpen(true);
                setTimeout(() => searchInputRef.current?.focus(), 50);
              }}
              aria-label="Search"
              title="Search"
              className={`${iconBtnCls} md:hidden`}
            >
              <i className="fas fa-search text-lg" aria-hidden="true" />
            </button>

            <button
              onClick={toggleDark}
              aria-label={darkMode ? 'Light mode' : 'Dark mode'}
              title={darkMode ? 'Light mode' : 'Dark mode'}
              className={iconBtnCls}
            >
              <i
                className={`fas ${darkMode ? 'fa-sun' : 'fa-moon'} text-lg`}
                aria-hidden="true"
              />
            </button>

            {user && isUserApproved && (
              <div className="relative hidden sm:block" ref={notifDropdownRef}>
                <button
                  onClick={() => setNotifDropdownOpen((o) => !o)}
                  aria-label={`Notifications${
                    unreadCount > 0 ? ` (${unreadCount} unread)` : ''
                  }`}
                  aria-haspopup="menu"
                  aria-expanded={notifDropdownOpen}
                  title="Notifications"
                  className={iconBtnCls}
                >
                  <i className="fas fa-bell text-lg" aria-hidden="true" />
                  {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                <AnimatePresence>
                  {notifDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -6, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -6, scale: 0.98 }}
                      transition={{ duration: 0.15 }}
                      role="menu"
                      className="absolute right-0 mt-2 w-80 sm:w-96 bg-white dark:bg-gray-800 rounded-xl shadow-2xl ring-1 ring-black/5 dark:ring-white/10 z-50 overflow-hidden"
                    >
                      <div className="px-4 py-3 border-b dark:border-gray-700 flex justify-between items-center">
                        <h3 className="text-base font-bold text-gray-800 dark:text-white">
                          Notifications
                        </h3>
                        {unreadCount > 0 && (
                          <button
                            onClick={markAllAsRead}
                            className="text-xs text-orange hover:underline"
                          >
                            Mark all as read
                          </button>
                        )}
                      </div>
                      <div className="max-h-96 overflow-y-auto">
                        {notifications.length === 0 ? (
                          <div className="p-8 text-center text-sm text-gray-500">
                            <i
                              className="fas fa-bell-slash text-3xl mb-2 block opacity-40"
                              aria-hidden="true"
                            />
                            No notifications
                          </div>
                        ) : (
                          groupedNotifications.map((group) => (
                            <div key={group.label}>
                              <div className="px-4 py-1.5 bg-gray-50 dark:bg-gray-800/60 text-[10px] uppercase tracking-wider font-semibold text-gray-500 sticky top-0">
                                {group.label}
                              </div>
                              {group.items.map((notif) => {
                                const unread = !notif.readBy?.includes(
                                  user.uid,
                                );
                                const createdAt =
                                  notif.createdAt?.toDate?.();
                                return (
                                  <button
                                    key={notif.id}
                                    onClick={() =>
                                      handleNotificationClick(notif)
                                    }
                                    role="menuitem"
                                    className={`w-full text-left px-4 py-3 border-b dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                                      unread ? 'bg-orange/5' : ''
                                    }`}
                                  >
                                    <div className="flex items-start gap-3">
                                      <div
                                        className="w-2 h-2 rounded-full bg-orange mt-2 flex-shrink-0"
                                        style={{ opacity: unread ? 1 : 0 }}
                                      />
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold text-gray-800 dark:text-white truncate">
                                          {notif.title}
                                        </p>
                                        <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 mt-0.5">
                                          {notif.body}
                                        </p>
                                        {createdAt && (
                                          <span className="text-[10px] text-gray-400 block mt-1">
                                            {createdAt.toLocaleString()}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          ))
                        )}
                      </div>
                      <Link
                        to="/notifications"
                        onClick={() => setNotifDropdownOpen(false)}
                        className="block px-4 py-3 border-t dark:border-gray-700 text-center text-xs text-orange hover:underline font-medium bg-gray-50 dark:bg-gray-800/50"
                      >
                        View all notifications
                      </Link>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {user && isUserApproved && (
              <Link
                to="/chat"
                aria-label={`Messages${
                  totalUnreadCount > 0
                    ? ` (${totalUnreadCount} unread)`
                    : ''
                }`}
                title="Messages"
                className={`${iconBtnCls} hidden sm:flex`}
              >
                <i
                  className="fas fa-comment-dots text-lg"
                  aria-hidden="true"
                />
                {totalUnreadCount > 0 && (
                  <span className="absolute top-1 right-1 bg-orange text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">
                    {totalUnreadCount > 9 ? '9+' : totalUnreadCount}
                  </span>
                )}
              </Link>
            )}

            {user ? (
              <div
                className="relative hidden sm:block"
                ref={profileDropdownRef}
              >
                <button
                  onClick={() => setProfileDropdownOpen((o) => !o)}
                  aria-haspopup="menu"
                  aria-expanded={profileDropdownOpen}
                  aria-label="Account menu"
                  className="flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-orange rounded-full"
                >
                  <div className="relative">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange to-orange-dark text-white flex items-center justify-center font-semibold text-sm overflow-hidden ring-2 ring-transparent hover:ring-orange/30 transition-all">
                      {userPhoto ? (
                        <img
                          src={userPhoto}
                          alt=""
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        getUserInitial()
                      )}
                    </div>
                    {isSelfOnline && isUserApproved && (
                      <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white dark:border-gray-900 rounded-full" />
                    )}
                  </div>
                </button>

                <AnimatePresence>
                  {profileDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -6, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -6, scale: 0.98 }}
                      transition={{ duration: 0.15 }}
                      role="menu"
                      className="absolute right-0 top-full mt-2 w-72 bg-white dark:bg-gray-800 rounded-xl shadow-2xl ring-1 ring-black/5 dark:ring-white/10 z-50 overflow-hidden"
                    >
                      <div className="p-4 border-b dark:border-gray-700">
                        <Link
                          to="/profile"
                          onClick={() => setProfileDropdownOpen(false)}
                          className="flex items-center gap-3 group"
                        >
                          <div className="relative flex-shrink-0">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange to-orange-dark text-white flex items-center justify-center font-bold text-lg overflow-hidden">
                              {userPhoto ? (
                                <img
                                  src={userPhoto}
                                  alt=""
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                />
                              ) : (
                                getUserInitial()
                              )}
                            </div>
                            {isSelfOnline && isUserApproved && (
                              <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white dark:border-gray-800 rounded-full" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-semibold text-sm text-gray-900 dark:text-white truncate group-hover:text-orange">
                              {getUserDisplayName()}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {getUserRole()}
                              {isUserPending && (
                                <span className="text-yellow-500 ml-1">
                                  · Pending
                                </span>
                              )}
                            </p>
                          </div>
                        </Link>
                        <Link
                          to="/profile"
                          onClick={() => setProfileDropdownOpen(false)}
                          className="mt-3 block w-full text-center text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 py-2 rounded-lg font-medium transition"
                        >
                          View Profile
                        </Link>
                      </div>

                      <div className="py-2">
                        {isUserApproved && isAdmin && (
                          <DropdownItem
                            to="/admin"
                            icon="fa-shield-halved"
                            label="Admin Panel"
                            onClick={() => setProfileDropdownOpen(false)}
                            accent
                          />
                        )}

                        <div className="my-2 border-t border-gray-200 dark:border-gray-700" />

                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        >
                          <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                            <i
                              className="fas fa-sign-out-alt text-xs"
                              aria-hidden="true"
                            />
                          </div>
                          Logout
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link
                to="/login"
                aria-label="Sign In"
                title="Sign In"
                className={`${iconBtnCls} hidden sm:flex`}
              >
                <i className="fas fa-user text-lg" aria-hidden="true" />
              </Link>
            )}

            <button
              className={`${iconBtnCls} lg:hidden`}
              aria-label="Toggle navigation menu"
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((o) => !o)}
            >
              <i
                className={`fas ${
                  mobileOpen ? 'fa-times' : 'fa-bars'
                } text-lg`}
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
      </nav>

      {/* ========== Mobile search overlay ========== */}
      <AnimatePresence>
        {searchOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-[60] md:hidden bg-white dark:bg-gray-900 flex flex-col"
          >
            <div className="p-3 flex items-center gap-3 border-b dark:border-gray-700">
              <div className="relative flex-1">
                <i
                  className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm"
                  aria-hidden="true"
                />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setShowSearchResults(true);
                  }}
                  onKeyDown={handleSearchKeyDown}
                  placeholder="Search..."
                  autoFocus
                  autoComplete="off"
                  className="w-full pl-10 pr-3 py-2 rounded-full bg-gray-100 dark:bg-gray-800 dark:text-white focus:outline-none"
                />
              </div>
              <button
                onClick={() => {
                  setSearchOpen(false);
                  setSearchTerm('');
                }}
                className="text-sm text-orange font-medium"
              >
                Cancel
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {searchTerm.trim().length < 2 && recentSearches.length > 0 && (
                <>
                  <div className="px-4 py-2 flex items-center justify-between">
                    <p className="text-[11px] uppercase tracking-wider font-semibold text-gray-500">
                      Recent
                    </p>
                    <button
                      onClick={clearAllRecent}
                      className="text-[11px] text-orange hover:underline"
                    >
                      Clear all
                    </button>
                  </div>
                  <div className="px-4 pb-3 flex flex-wrap gap-1.5">
                    {recentSearches.map((term) => (
                      <button
                        key={term}
                        onClick={() => applyRecentSearch(term)}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200"
                      >
                        <i
                          className="fas fa-history text-[10px] text-gray-400"
                          aria-hidden="true"
                        />
                        {term}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {searchTerm.trim().length >= 2 && (
                <div className={darkMode ? 'bg-gray-900' : 'bg-white'}>
                  {renderSearchDropdown()}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ========== Mobile menu overlay ========== */}
      <AnimatePresence>
        {mobileOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setMobileOpen(false)}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 320, damping: 32 }}
              className={`absolute right-0 top-0 h-full w-4/5 max-w-sm ${
                darkMode ? 'bg-gray-900' : 'bg-white'
              } shadow-2xl overflow-y-auto pb-24`}
            >
              <div className="p-4">
                <div className="flex justify-end mb-4">
                  <button
                    onClick={() => setMobileOpen(false)}
                    aria-label="Close menu"
                    className="w-10 h-10 rounded-full flex items-center justify-center text-gray-500 hover:text-orange hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    <i
                      className="fas fa-times text-lg"
                      aria-hidden="true"
                    />
                  </button>
                </div>

                {user ? (
                  <>
                    <Link
                      to="/profile"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 mb-2"
                    >
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange to-orange-dark text-white flex items-center justify-center font-bold text-lg overflow-hidden flex-shrink-0">
                        {userPhoto ? (
                          <img
                            src={userPhoto}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          getUserInitial()
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold dark:text-white truncate">
                          {getUserDisplayName()}
                        </p>
                        <p className="text-xs text-gray-500">
                          {getUserRole()}
                          {isUserPending && (
                            <span className="text-yellow-500 ml-1">
                              · Pending
                            </span>
                          )}
                        </p>
                      </div>
                    </Link>

                    <div className="border-t dark:border-gray-700 pt-2">
                      <MobileItem
                        to="/"
                        icon="fa-home"
                        label="Home"
                        onClick={() => setMobileOpen(false)}
                      />
                      {isUserApproved && (
                        <MobileItem
                          to="/media"
                          icon="fa-images"
                          label="Media"
                          onClick={() => setMobileOpen(false)}
                        />
                      )}
                      {isUserApproved && (
                        <MobileItem
                          to="/members"
                          icon="fa-user-group"
                          label="Members"
                          onClick={() => setMobileOpen(false)}
                        />
                      )}
                      <MobileItem
                        to="/board"
                        icon="fa-users"
                        label="Board of Directors"
                        onClick={() => setMobileOpen(false)}
                      />
                      <MobileItem
                        to="/toolkit"
                        icon="fa-calculator"
                        label="EE Toolkit"
                        onClick={() => setMobileOpen(false)}
                      />
                      <MobileItem
                        to="/contact"
                        icon="fa-envelope"
                        label="Contact"
                        onClick={() => setMobileOpen(false)}
                      />
                      <MobileItem
                        to="/about"
                        icon="fa-circle-info"
                        label="About"
                        onClick={() => setMobileOpen(false)}
                      />

                      {isUserApproved && (
                        <>
                          <MobileItem
                            to="/chat"
                            icon="fa-comment-dots"
                            label="Messages"
                            badge={totalUnreadCount}
                            onClick={() => setMobileOpen(false)}
                          />
                          <MobileItem
                            to="/notifications"
                            icon="fa-bell"
                            label="Notifications"
                            badge={unreadCount}
                            onClick={() => setMobileOpen(false)}
                          />
                        </>
                      )}

                      <MobileItem
                        to="/settings"
                        icon="fa-cog"
                        label="Settings"
                        onClick={() => setMobileOpen(false)}
                      />

                      {isUserApproved && isAdmin && (
                        <MobileItem
                          to="/admin"
                          icon="fa-shield-halved"
                          label="Admin Panel"
                          onClick={() => setMobileOpen(false)}
                        />
                      )}

                      <button
                        onClick={() => {
                          setMobileOpen(false);
                          handleLogout();
                        }}
                        className="w-full flex items-center gap-3 p-3 rounded-xl text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      >
                        <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                          <i
                            className="fas fa-sign-out-alt"
                            aria-hidden="true"
                          />
                        </div>
                        <span className="font-medium">Logout</span>
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="border-t dark:border-gray-700 pt-2">
                    <MobileItem
                      to="/"
                      icon="fa-home"
                      label="Home"
                      onClick={() => setMobileOpen(false)}
                    />
                    <MobileItem
                      to="/board"
                      icon="fa-users"
                      label="Board of Directors"
                      onClick={() => setMobileOpen(false)}
                    />
                    <MobileItem
                      to="/toolkit"
                      icon="fa-calculator"
                      label="EE Toolkit"
                      onClick={() => setMobileOpen(false)}
                    />
                    <MobileItem
                      to="/contact"
                      icon="fa-envelope"
                      label="Contact"
                      onClick={() => setMobileOpen(false)}
                    />
                    <MobileItem
                      to="/about"
                      icon="fa-circle-info"
                      label="About"
                      onClick={() => setMobileOpen(false)}
                    />
                    <MobileItem
                      to="/login"
                      icon="fa-sign-in-alt"
                      label="Sign In"
                      onClick={() => setMobileOpen(false)}
                    />
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ========== Mobile bottom navigation ========== */}
      {!mobileOpen && !searchOpen && (
        <MobileBottomNav
          darkMode={darkMode}
          isUserApproved={isUserApproved}
          user={user}
          location={location}
          totalUnreadCount={totalUnreadCount}
          unreadCount={unreadCount}
        />
      )}
    </>
  );
};

/* ================================================================== */
/* Mobile Bottom Navigation — uniform tabs (no center FAB)             */
/* ================================================================== */

const MobileBottomNav = memo(
  ({
    darkMode,
    isUserApproved,
    user,
    location,
    totalUnreadCount,
    unreadCount,
  }) => {
    const tabs = useMemo(
      () =>
        [
          {
            id: 'home',
            to: '/',
            icon: 'fa-home',
            label: 'Home',
            active: location.pathname === '/',
          },
          {
            id: 'media',
            to: '/media',
            icon: 'fa-images',
            label: 'Media',
            active: location.pathname.startsWith('/media'),
            hidden: !isUserApproved,
          },
          {
            id: 'messages',
            to: '/chat',
            icon: 'fa-comment-dots',
            label: 'Messages',
            active: location.pathname.startsWith('/chat'),
            badge: totalUnreadCount,
            hidden: !isUserApproved,
          },
          {
            id: 'notifications',
            to: '/notifications',
            icon: 'fa-bell',
            label: 'Alerts',
            active: location.pathname.startsWith('/notifications'),
            badge: unreadCount,
            hidden: !isUserApproved,
          },
          {
            id: 'profile',
            to: user ? '/profile' : '/login',
            icon: 'fa-user',
            label: user ? 'Profile' : 'Sign In',
            active: location.pathname.startsWith('/profile'),
          },
        ].filter((t) => !t.hidden),
      [
        location.pathname,
        isUserApproved,
        user,
        unreadCount,
        totalUnreadCount,
      ],
    );

    const glassCls = darkMode
      ? 'bg-gray-900/65 border border-white/10'
      : 'bg-white/65 border border-white/60';

    return (
      <nav
        className="fixed inset-x-0 bottom-0 z-40 lg:hidden pointer-events-none"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        aria-label="Primary"
      >
        <div className="px-3 pb-3 pt-2">
          <ul
            className={`pointer-events-auto flex items-stretch justify-around rounded-3xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] backdrop-blur-2xl backdrop-saturate-150 ${glassCls}`}
          >
            {tabs.map((t) => (
              <li key={t.id} className="flex-1">
                <Link
                  to={t.to}
                  className={`relative flex flex-col items-center justify-center h-full py-2.5 transition-colors duration-150 ${
                    t.active
                      ? 'text-orange'
                      : darkMode
                      ? 'text-gray-400 hover:text-white'
                      : 'text-gray-500 hover:text-gray-900'
                  }`}
                  aria-current={t.active ? 'page' : undefined}
                >
                  {t.active && (
                    <span
                      className="absolute top-1.5 h-8 w-14 rounded-2xl bg-orange/12 dark:bg-orange/20"
                      aria-hidden="true"
                    />
                  )}

                  <span className="relative flex items-center justify-center">
                    <i
                      className={`fas ${t.icon} text-[18px] transition-transform duration-150 ${
                        t.active ? 'scale-110' : ''
                      }`}
                      aria-hidden="true"
                    />
                    {t.badge > 0 && (
                      <span className="absolute -top-1.5 -right-2 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center ring-2 ring-white/80 dark:ring-gray-900/80">
                        {t.badge > 9 ? '9+' : t.badge}
                      </span>
                    )}
                  </span>
                  <span
                    className={`text-[10px] mt-0.5 ${
                      t.active ? 'font-semibold' : 'font-medium'
                    }`}
                  >
                    {t.label}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </nav>
    );
  },
);

MobileBottomNav.displayName = 'MobileBottomNav';

/* ================================================================== */
/* Reusable menu items                                                 */
/* ================================================================== */

const DropdownItem = ({ to, icon, label, onClick, accent = false }) => (
  <Link
    to={to}
    onClick={onClick}
    role="menuitem"
    className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
      accent
        ? 'text-orange hover:bg-orange/10 font-medium'
        : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
    }`}
  >
    <div
      className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
        accent
          ? 'bg-orange/10 text-orange'
          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
      }`}
    >
      <i className={`fas ${icon} text-sm`} aria-hidden="true" />
    </div>
    {label}
  </Link>
);

const MobileItem = ({ to, icon, label, badge = 0, onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
  >
    <div className="relative w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
      <i
        className={`fas ${icon} text-gray-600 dark:text-gray-300`}
        aria-hidden="true"
      />
      {badge > 0 && (
        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">
          {badge > 9 ? '9+' : badge}
        </span>
      )}
    </div>
    <span className="font-medium dark:text-white">{label}</span>
  </Link>
);

export { POST_COMPOSER_EVENT };
export default Navbar;